/**
 * Crop editor (modal): fixed frame + pan + zoom + rotate 90°.
 *
 * Works entirely on COORDINATES - no pixels are exported. The result is
 * crop = { x, y, width, height, rotate } in the pixel space of the uploaded
 * file, ROTATE-FIRST: the server rotates by `rotate`, then extracts the rect.
 * Because nothing is read back from the canvas/image, cross-origin originals
 * (re-cropping an already stored file) work without CORS headers.
 */
const t = (s) => (window.g && window.g.translate ? window.g.translate(s) : s);

const STAGE_W = 460;
const STAGE_H = 340;

// aspect option: '' = the image's own ratio, 'W:H' or a number (w/h).
function frameSize(aspect, imgW, imgH) {
	let ratio = imgW / imgH;
	if (typeof aspect === 'number' && aspect > 0) ratio = aspect;
	else if (typeof aspect === 'string' && aspect.includes(':')) {
		const [w, h] = aspect.split(':').map(Number);
		if (w > 0 && h > 0) ratio = w / h;
	}
	let width = STAGE_W - 40;
	let height = width / ratio;
	if (height > STAGE_H - 40) { height = STAGE_H - 40; width = height * ratio; }
	return { width, height, ratio };
}

// openEditor({ url, crop?, aspect, onApply }) - builds, shows and disposes a
// bootstrap modal; calls onApply(crop) when confirmed.
export function openEditor({ url, crop, aspect, round, onApply }) {
	const modal = document.createElement('div');
	modal.className = 'modal fade fsel-editor';
	modal.tabIndex = -1;
	modal.innerHTML = `<div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="fsel-editor-head"><span class="fsel-editor-title">${t('Crop image')}</span><button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('Close')}"></button></div><div class="fsel-editor-stage"><div class="fsel-editor-frame"><img class="fsel-editor-img" alt="" /></div></div><div class="fsel-editor-controls"><i class="ri-zoom-out-line" aria-hidden="true"></i><input type="range" class="form-range fsel-editor-zoom" min="1" max="4" step="0.01" value="1" aria-label="${t('Zoom')}" /><i class="ri-zoom-in-line" aria-hidden="true"></i><button type="button" class="btn btn-sm btn-secondary fsel-editor-rotate" aria-label="${t('Rotate')}"><i class="ri-anticlockwise-2-line" aria-hidden="true"></i></button></div><div class="fsel-editor-foot"><button type="button" class="btn btn-sm btn-soft-outline-secondary me-auto" data-bs-dismiss="modal">${t('Cancel')}</button><button type="button" class="btn btn-sm btn-primary fsel-editor-apply">${t('Apply')}</button></div></div></div>`;
	document.body.appendChild(modal);

	const img = modal.querySelector('.fsel-editor-img');
	const frame = modal.querySelector('.fsel-editor-frame');
	if (round) frame.classList.add('is-round'); // circle avatars: round mask (coords stay square)
	const zoomInput = modal.querySelector('.fsel-editor-zoom');
	const state = { rotate: (crop && crop.rotate) || 0, zoom: 1, tx: 0, ty: 0, W: 0, H: 0, frameW: 0, frameH: 0, minScale: 1 };

	const rotatedSize = () => (state.rotate % 180 === 0 ? { w: state.W, h: state.H } : { w: state.H, h: state.W });
	const scaleNow = () => state.minScale * state.zoom;

	function clampPan() {
		const { w, h } = rotatedSize();
		const maxX = Math.max(0, (w * scaleNow() - state.frameW) / 2);
		const maxY = Math.max(0, (h * scaleNow() - state.frameH) / 2);
		state.tx = Math.min(maxX, Math.max(-maxX, state.tx));
		state.ty = Math.min(maxY, Math.max(-maxY, state.ty));
	}

	function paint() {
		clampPan();
		img.style.transform = `translate(-50%, -50%) translate(${state.tx}px, ${state.ty}px) rotate(${state.rotate}deg) scale(${scaleNow()})`;
	}

	function fit() {
		const { w, h } = rotatedSize();
		state.minScale = Math.max(state.frameW / w, state.frameH / h);
		paint();
	}

	// current view -> crop rect in rotated-image pixels. Clamped so the rect
	// NEVER leaves the image (x + width <= w) - zoom/pan rounding used to
	// overflow by a pixel or two and the server refuses out-of-bounds rects.
	function currentCrop() {
		const { w, h } = rotatedSize();
		const scale = scaleNow();
		const width = Math.min(w, Math.round(state.frameW / scale));
		const height = Math.min(h, Math.round(state.frameH / scale));
		const x = (w - width) / 2 - state.tx / scale;
		const y = (h - height) / 2 - state.ty / scale;
		return {
			x: Math.min(Math.max(0, Math.round(x)), w - width),
			y: Math.min(Math.max(0, Math.round(y)), h - height),
			width,
			height,
			rotate: ((state.rotate % 360) + 360) % 360,
		};
	}

	// existing crop -> restore zoom/pan so reopening shows the same view.
	// A tight crop on a large image needs more zoom than the slider's
	// default max — extend the max instead of clamping, or the restored
	// view silently shows a WIDER frame than the saved crop.
	function restore(fromCrop) {
		const { w, h } = rotatedSize();
		const scale = state.frameW / fromCrop.width;
		const needed = Math.max(1, scale / state.minScale);
		if (needed > parseFloat(zoomInput.max)) zoomInput.max = String(Math.ceil(needed));
		state.zoom = needed;
		const s = scaleNow();
		state.tx = ((w - fromCrop.width) / 2 - fromCrop.x) * s;
		state.ty = ((h - fromCrop.height) / 2 - fromCrop.y) * s;
		zoomInput.value = state.zoom;
		paint();
	}

	img.addEventListener('load', () => {
		state.W = img.naturalWidth;
		state.H = img.naturalHeight;
		const size = frameSize(aspect, ...(state.rotate % 180 === 0 ? [state.W, state.H] : [state.H, state.W]));
		state.frameW = size.width;
		state.frameH = size.height;
		frame.style.width = `${size.width}px`;
		frame.style.height = `${size.height}px`;
		fit();
		if (crop && crop.width) restore(crop);
	});
	img.src = url;

	// pan (pointer events cover mouse + touch)
	let dragging = null;
	frame.addEventListener('pointerdown', (e) => {
		dragging = { x: e.clientX - state.tx, y: e.clientY - state.ty };
		frame.setPointerCapture(e.pointerId);
	});
	frame.addEventListener('pointermove', (e) => {
		if (!dragging) return;
		state.tx = e.clientX - dragging.x;
		state.ty = e.clientY - dragging.y;
		paint();
	});
	frame.addEventListener('pointerup', () => { dragging = null; });

	zoomInput.addEventListener('input', () => { state.zoom = parseFloat(zoomInput.value); paint(); });
	modal.querySelector('.fsel-editor-rotate').addEventListener('click', () => {
		state.rotate = (state.rotate + 90) % 360;
		state.tx = 0;
		state.ty = 0;
		const size = frameSize(aspect, ...(state.rotate % 180 === 0 ? [state.W, state.H] : [state.H, state.W]));
		state.frameW = size.width;
		state.frameH = size.height;
		frame.style.width = `${size.width}px`;
		frame.style.height = `${size.height}px`;
		fit();
	});

	modal.querySelector('.fsel-editor-apply').addEventListener('click', () => {
		onApply(currentCrop());
		bs.hide();
	});
	modal.addEventListener('hidden.bs.modal', () => { bs.dispose(); modal.remove(); });

	const bs = window.bootstrap.Modal.getOrCreateInstance(modal, { backdrop: 'static' });
	bs.show();
}
